import React, { useRef, useEffect, useMemo } from 'react';
import Plotly from 'plotly.js-dist';
import type { TimelineDataPoint } from '../api/fe-management-system-api/narratives';

export const darkLayoutBase = {
  margin: { l: 72, r: 24, b: 50, t: 56 },
  plot_bgcolor: 'rgba(17,24,39,0.4)',
  paper_bgcolor: 'rgba(0,0,0,0)',
  font: { color: '#E5E7EB', size: 11 },
  xaxis: {
    showgrid: true,
    gridcolor: '#374151',
    tickfont: { color: '#9CA3AF' },
    type: 'date' as const,
  },
  yaxis: {
    showgrid: true,
    gridcolor: '#374151',
    tickfont: { color: '#9CA3AF' },
  },
  legend: {
    orientation: 'h',
    y: 1.12,
    font: { color: '#9CA3AF', size: 10 },
  },
  hovermode: 'x unified' as const,
};

export const NarrativeTotalMcapChart: React.FC<{ dataPoints: TimelineDataPoint[] }> = ({ dataPoints }) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const x = useMemo(() => dataPoints.map((p) => p.timestamp), [dataPoints]);
  const y = useMemo(() => dataPoints.map((p) => p.totalMarketCap), [dataPoints]);
  const trace = useMemo(
    () => ({
      x,
      y,
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: 'Total MC',
      line: { color: '#3B82F6', width: 2 },
      hovertemplate: '%{x|%b %d, %Y, %H:%M}<br>Total: $%{y:,.0f}<extra></extra>',
    }),
    [x, y]
  );
  const layout = useMemo(
    () => ({
      ...darkLayoutBase,
      title: { text: 'Total Market Cap Timeline', font: { color: '#FFF', size: 14 }, x: 0.05 },
      yaxis: { ...darkLayoutBase.yaxis, tickprefix: '$', tickformat: ',.0f' },
    }),
    []
  );
  useEffect(() => {
    const plotEl = plotRef.current;
    if (!plotEl || dataPoints.length === 0) return;
    Plotly.newPlot(plotEl, [trace] as any, layout as any, { displayModeBar: false, responsive: true });
    return () => {
      if (plotEl) Plotly.purge(plotEl);
    };
  }, [trace, layout, dataPoints.length]);
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/30 p-4">
      <div ref={plotRef} className="h-[380px] w-full" />
    </div>
  );
};

const STACKED_CHART_COLORS = ['#3B82F6', '#22C55E', '#EAB308', '#EF4444', '#A855F7', '#EC4899', '#14B8A6', '#F97316'];

export const NarrativeStackedMcapChart: React.FC<{ dataPoints: TimelineDataPoint[] }> = ({ dataPoints }) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const symbols = useMemo(() => {
    const set = new Set<string>();
    dataPoints.forEach((p) => p.tokenMarketCaps.forEach((t) => set.add(t.symbol)));
    return Array.from(set);
  }, [dataPoints]);
  const traces = useMemo(() => {
    const x = dataPoints.map((p) => p.timestamp);
    return symbols.map((symbol, i) => {
      const y = dataPoints.map((p) => {
        const token = p.tokenMarketCaps.find((t) => t.symbol === symbol);
        return token?.marketCap ?? 0;
      });
      return {
        x,
        y,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: symbol,
        stackgroup: 'one',
        fill: 'tonexty' as const,
        line: { width: 0 },
        fillcolor: STACKED_CHART_COLORS[i % STACKED_CHART_COLORS.length] + '99',
        hovertemplate: `${symbol}<br>%{x|%b %d, %Y}<br>$%{y:,.0f}<extra></extra>`,
      };
    });
  }, [dataPoints, symbols]);
  const layout = useMemo(
    () => ({
      ...darkLayoutBase,
      title: { text: 'Token Market Cap Breakdown (Stacked)', font: { color: '#FFF', size: 14 }, x: 0.05 },
      yaxis: { ...darkLayoutBase.yaxis, tickprefix: '$', tickformat: ',.0f' },
    }),
    []
  );
  useEffect(() => {
    const plotEl = plotRef.current;
    if (!plotEl || traces.length === 0) return;
    Plotly.newPlot(plotEl, traces as any, layout as any, { displayModeBar: false, responsive: true });
    return () => {
      if (plotEl) Plotly.purge(plotEl);
    };
  }, [traces, layout]);
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/30 p-4">
      <div ref={plotRef} className="h-[380px] w-full" />
    </div>
  );
};

const LINE_CHART_COLORS = ['#3B82F6', '#22C55E', '#EAB308', '#EF4444', '#A855F7'];

export const NarrativeIndividualMcapChart: React.FC<{ dataPoints: TimelineDataPoint[] }> = ({ dataPoints }) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const symbols = useMemo(() => {
    const set = new Set<string>();
    dataPoints.forEach((p) => p.tokenMarketCaps.forEach((t) => set.add(t.symbol)));
    return Array.from(set);
  }, [dataPoints]);
  const traces = useMemo(() => {
    return symbols.map((symbol, i) => {
      const x = dataPoints.map((p) => p.timestamp);
      const y = dataPoints.map((p) => {
        const token = p.tokenMarketCaps.find((t) => t.symbol === symbol);
        return token?.marketCap ?? 0;
      });
      return {
        x,
        y,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: symbol,
        line: { color: LINE_CHART_COLORS[i % LINE_CHART_COLORS.length], width: 2 },
        hovertemplate: `${symbol}<br>%{x|%b %d, %Y}<br>$%{y:,.0f}<extra></extra>`,
      };
    });
  }, [dataPoints, symbols]);
  const layout = useMemo(
    () => ({
      ...darkLayoutBase,
      title: {
        text: 'Individual Token Market Caps (for Flip Detection)',
        font: { color: '#FFF', size: 14 },
        x: 0.05,
      },
      yaxis: { ...darkLayoutBase.yaxis, tickprefix: '$', tickformat: ',.0f' },
    }),
    []
  );
  useEffect(() => {
    const plotEl = plotRef.current;
    if (!plotEl || traces.length === 0) return;
    Plotly.newPlot(plotEl, traces as any, layout as any, { displayModeBar: false, responsive: true });
    return () => {
      if (plotEl) Plotly.purge(plotEl);
    };
  }, [traces, layout]);
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/30 p-4">
      <div ref={plotRef} className="h-[380px] w-full" />
    </div>
  );
};

export const NarrativeTokenCountChart: React.FC<{ dataPoints: TimelineDataPoint[] }> = ({ dataPoints }) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const trace = useMemo(
    () => ({
      x: dataPoints.map((p) => p.timestamp),
      y: dataPoints.map((p) => p.tokenCount),
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: 'Token Count',
      line: { color: '#22C55E', width: 2, shape: 'hv' as const },
      fill: 'tozeroy' as const,
      fillcolor: '#22C55E33',
      hovertemplate: '%{x|%b %d, %Y}<br>Count: %{y}<extra></extra>',
    }),
    [dataPoints]
  );
  const layout = useMemo(
    () => ({
      ...darkLayoutBase,
      title: { text: 'Token Count Over Time', font: { color: '#FFF', size: 14 }, x: 0.05 },
      yaxis: { ...darkLayoutBase.yaxis, dtick: 1, tickformat: 'd' },
    }),
    []
  );
  useEffect(() => {
    const plotEl = plotRef.current;
    if (!plotEl || dataPoints.length === 0) return;
    Plotly.newPlot(plotEl, [trace] as any, layout as any, { displayModeBar: false, responsive: true });
    return () => {
      if (plotEl) Plotly.purge(plotEl);
    };
  }, [trace, layout, dataPoints.length]);
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/30 p-4">
      <div ref={plotRef} className="h-[340px] w-full" />
    </div>
  );
};
